<template>
  <b-container>
    <h3>Active Demands</h3>
    <span v-if="cancellationErrors.length">
      <b-row v-for="(error, index) in cancellationErrors" :key="index">
        <b-col>
          <b-alert show variant="danger">{{ error.message }}</b-alert>
        </b-col>
      </b-row>
      <b-row class="mb-5">
        <b-col>
          <b-button @click="clearErrors()" variant="info">Clear errors list</b-button>
        </b-col>
      </b-row>
    </span>
    <b-row>
      <b-col md="6" class="my-1">
        <b-form-group label-cols-sm="3" label="Filter" class="mb-0">
          <b-input-group>
            <b-form-input v-model="filter" placeholder="Type to Search" />
            <b-input-group-append>
              <b-button :disabled="!filter" @click="filter = ''">Clear </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>
      <b-col md="6" class="text-right">
        <b-button-group>
          <b-dropdown size="md" variant="secondary" text="Download list">
            <b-dropdown-item-button
              @click="
                downloadList([
                  'variantKey',
                  'productKey',
                  'brand',
                  'model',
                  'referenceNumber',
                  'dial',
                  'bracelet',
                  'demandQuantity',
                  'status',
                  'orderNumber',
                  'diffTaxPossible',
                  'createdAt',
                  'type',
                ])
              "
              >Internal</b-dropdown-item-button
            >
            <b-dropdown-divider />
            <b-dropdown-item-button
              @click="downloadList(['brand', 'referenceNumber', 'dial', 'bracelet', 'demandQuantity'], null, true)"
              >Supplier</b-dropdown-item-button
            >
            <b-dropdown-item-button
              @click="downloadList(['brand', 'referenceNumber', 'dial', 'bracelet', 'demandQuantity'], true, true)"
            >
              Supplier DiffTax
            </b-dropdown-item-button>
            <b-dropdown-item-button
              @click="
                downloadListGrouped(
                  ['brand', 'referenceNumber', 'dial', 'bracelet', 'amount', 'diffTaxPossible'],
                  true,
                  true,
                )
              "
            >
              Supplier Grouped DiffTax
            </b-dropdown-item-button>
          </b-dropdown>
          <b-button variant="primary" @click="reloadData">Reload list <b-icon-arrow-repeat/></b-button>
        </b-button-group>
      </b-col>
    </b-row>

    <b-table
      striped
      hover
      show-empty
      :filter="filter"
      @filtered="onFiltered"
      :items="demands"
      :fields="fields"
      :current-page="currentPage"
      :per-page="pageSize"
      :busy="!isBusy"
    >
      <div slot="table-busy" class="text-center my-2">
        <b-spinner class="align-middle" />
        <strong>Loading...</strong>
      </div>

      <template v-slot:cell(createdAt)="data">
        {{ formattedDate(data.item.createdAt) }}
      </template>

      <template v-slot:cell(type)="data">
        <b-badge variant="success" v-if="data.item.type === DEMAND.TYPE.DIVA">DiVA</b-badge>
        <b-badge variant="warning" v-else-if="data.item.type === DEMAND.TYPE.STRATEGIC">SD</b-badge>
        <b-badge variant="info" v-else-if="data.item.type === DEMAND.TYPE.NEVER_OUT_OF_STOCK">NOS</b-badge>
      </template>

      <template v-slot:cell(actions)="data">
        <b-button size="sm" variant="danger" @click="openCancelDialog(data)">Cancel demand</b-button>
      </template>
    </b-table>
    <b-pagination :total-rows="totalSize" :per-page="pageSize" v-model="currentPage" align="center" />

    <b-modal v-model="showCancelDialog" centered size="xl" title="Are you sure to cancel this demand ?">
      <b-table
        v-if="demandToCancel !== null"
        striped
        stacked
        :items="[demandToCancel]"
        :fields="['demandId', 'productKey', 'variantKey', 'orderNumber', 'lineNumber', 'unitPrice', 'type']"
      >
        <template v-slot:cell(unitPrice)>
          {{ demandToCancel.unitPrice / 100 }} {{ demandToCancel.unitCurrency }}
        </template>
        <template v-slot:cell(type)>
          <b-badge variant="success" v-if="demandToCancel.type === DEMAND.TYPE.DIVA">DiVA </b-badge>
          <b-badge variant="warning" v-else-if="demandToCancel.type === DEMAND.TYPE.STRATEGIC">SD </b-badge>
          <b-badge variant="info" v-else-if="demandToCancel.type === DEMAND.TYPE.NEVER_OUT_OF_STOCK">NOS </b-badge>
        </template>
      </b-table>
      <div slot="modal-footer" class="w-100">
        <b-button size="sm" class="float-left" variant="danger" :disabled="cancelingDemand" @click="closeCancelDialog"
          >Close</b-button
        >
        <b-button
          size="sm"
          class="float-right"
          variant="success"
          :disabled="cancelingDemand"
          @click="confirmCancelDialog"
        >
          <span v-if="cancelingDemand"> <b-spinner small /> Canceling ... </span>
          <span v-else>
            Confirm
          </span>
        </b-button>
      </div>
    </b-modal>
  </b-container>
</template>

<script>
import { CANCEL_DEMAND_ACTION, LOAD, RELOAD, DOWNLOAD, DOWNLOADGROUPED } from 'src/store/actionTypes';
import DemandConstants from 'src/constants/demand';
import { timeFormatMixin } from 'Mixins/timeFormatMixin';
import { mapGetters, mapState, mapActions } from 'vuex';
import ApiLoadingStatus from '@/api/statusTypes';
import { GET_ACTIVE_DEMANDS } from '../../store/getterTypes';

export default {
  name: 'AdminDemandOverview',
  mixins: [timeFormatMixin],
  props: [],
  data: function() {
    return {
      fields: ['createdAt', 'variantKey', 'productKey', 'brand', 'model', 'orderNumber', 'type', 'actions'],
      currentPage: 1,
      pageSize: 20,
      filter: null,
      status: '',
      cancellationErrors: [],
      DEMAND: DemandConstants,
      showCancelDialog: false,
      demandToCancel: null,
      cancelingDemand: false,
    };
  },
  mounted() {
    this.getDemands();
  },
  computed: {
    ...mapGetters('demands', {
      activeDemands: GET_ACTIVE_DEMANDS,
    }),
    ...mapState('demands', {
      lastUpdate: (state) => state.lastUpdate,
      demandStatus: (state) => state.status,
    }),
    demands: function() {
      return this.activeDemands;
    },
    totalSize() {
      return this.demands.length;
    },
    isBusy() {
      return this.demandStatus === ApiLoadingStatus.STATUS_SUCCESSFUL;
    },
  },
  methods: {
    ...mapActions('demands', {
      getDemands: LOAD,
      downloadDemands: DOWNLOAD,
      downloadDemandsGrouped: DOWNLOADGROUPED,
      reloadDemands: RELOAD,
      cancel2Demand: CANCEL_DEMAND_ACTION,
    }),
    openCancelDialog(demand) {
      this.demandToCancel = demand.item;
      this.demandToCancel._showDetails = false;
      this.showCancelDialog = true;
    },
    confirmCancelDialog() {
      this.cancel2Demand({ demandId: this.demandToCancel.demandId });
      this.closeCancelDialog();
    },
    closeCancelDialog() {
      this.demandToCancel = null;
      this.showCancelDialog = false;
    },
    clearErrors() {
      this.cancellationErrors = [];
    },
    downloadList(fields, diffTax = null, active = null) {
      if (undefined === fields) {
        fields = [];
      }
      this.downloadDemands({ fields: fields, diffTax: diffTax, active: active });
    },
    downloadListGrouped(fields, diffTax = null, active = null) {
      if (undefined === fields) {
        fields = [];
      }
      this.downloadDemandsGrouped({
        fields: fields,
        diffTax: diffTax,
        active: active,
      });
    },
    reloadData() {
      this.reloadDemands();
    },
    onFiltered(filteredItems) {
      this.totalSize = filteredItems.length;
      this.currentPage = 1;
    },
  },
};
</script>
